/**
 * SubmissionForm — student uploads file(s) + optional text for an assignment.
 * 3-step flow: upload-url → PUT R2 → confirm.
 * Disabled/hidden when submission.status === 'graded'.
 * Parent: read-only (no form rendered).
 */
import { useState } from 'react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { FileUpload } from './file-upload';
import { api } from '@client/lib/api-client';

interface UploadUrlResponse {
  fileKey: string;
  uploadUrl: string;
}

interface SubmissionFormProps {
  submissionId: string;
  isParent: boolean;
  existingStatus?: string | null;
  existingTextContent?: string | null;
  onSuccess: () => void;
}

const STATUS_LABEL: Record<string, { label: string; color: string }> = {
  assigned: { label: 'Chưa nộp', color: 'bg-secondary text-secondary-foreground' },
  submitted: { label: 'Đã nộp', color: 'bg-[var(--color-info)]/10 text-[var(--color-info)] dark:bg-[var(--color-info-border)]/15 dark:text-[var(--color-info-border)]' },
  late: { label: 'Nộp muộn', color: 'bg-brand-orange-100 text-brand-orange-700 dark:bg-brand-orange-950/50 dark:text-brand-orange-300' },
  graded: { label: 'Đã chấm', color: 'bg-brand-green-100 text-brand-green-700 dark:bg-brand-green-900/50 dark:text-brand-green-300' },
};

export function SubmissionForm({
  submissionId,
  isParent,
  existingStatus,
  existingTextContent,
  onSuccess,
}: SubmissionFormProps) {
  const [textContent, setTextContent] = useState(existingTextContent ?? '');
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const isGraded = existingStatus === 'graded';

  // Parent: show read-only status only
  if (isParent) {
    const s = STATUS_LABEL[existingStatus ?? 'assigned']!;
    return (
      <div className="flex items-center gap-2 py-2">
        <span className="text-sm text-muted-foreground">Trạng thái nộp:</span>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${s.color}`}>
          {s.label}
        </span>
      </div>
    );
  }

  // Graded: show locked state
  if (isGraded) {
    return (
      <div className="flex items-center gap-2 border border-border rounded-lg p-3 bg-muted/40">
        <Badge variant="success">Đã chấm</Badge>
        <span className="text-sm text-muted-foreground">Bài đã được chấm — không thể nộp lại</span>
      </div>
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmedText = textContent.trim();

    // Validate: must have text OR files
    if (!trimmedText && files.length === 0) {
      toast.error('Vui lòng nhập nội dung hoặc đính kèm ít nhất 1 file');
      return;
    }

    setUploading(true);
    setProgress(0);

    try {
      const uploadedKeys: Array<{ fileKey: string; fileName: string }> = [];

      // Step 1+2 per file: get upload-url → PUT to R2
      for (let i = 0; i < files.length; i++) {
        const file = files[i]!;

        // Step 1: request presigned URL
        const urlResp = await api.post<UploadUrlResponse>(
          `/api/submissions/${submissionId}/files/upload-url`,
          { fileName: file.name, fileSize: file.size, contentType: file.type },
        );

        // Step 2: PUT directly to R2
        const putResp = await fetch(urlResp.uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });

        if (!putResp.ok) {
          throw new Error(`Upload "${file.name}" thất bại (${putResp.status})`);
        }

        uploadedKeys.push({ fileKey: urlResp.fileKey, fileName: file.name });
        setProgress(Math.round(((i + 1) / (files.length + 1)) * 90));
      }

      // Step 3: confirm submission
      await api.post(`/api/submissions/${submissionId}/files`, {
        textContent: trimmedText || null,
        files: uploadedKeys.map(({ fileKey, fileName }) => ({ fileKey, fileName })),
      });

      setProgress(100);
      toast.success('Đã nộp bài thành công');
      setFiles([]);
      setTextContent('');
      onSuccess();
    } catch (err) {
      const e = err as { status?: number; message?: string };
      if (e.status === 409) {
        toast.error('Bài đã được chấm, không thể nộp lại');
      } else {
        toast.error(e.message ?? 'Nộp bài thất bại — vui lòng thử lại');
      }
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
      <div className="space-y-2">
        <label className="text-sm font-medium">Nội dung bài làm</label>
        <Textarea
          placeholder="Nhập nội dung bài làm (không bắt buộc nếu có file đính kèm)..."
          value={textContent}
          onChange={(e) => setTextContent(e.target.value)}
          rows={4}
          maxLength={5000}
          className="resize-none text-sm w-full"
          disabled={uploading}
        />
        <p className="text-xs text-muted-foreground text-right">{textContent.length}/5000</p>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Tệp đính kèm</label>
        <FileUpload files={files} onFilesChange={setFiles} disabled={uploading} />
      </div>

      {uploading && (
        <div className="space-y-1.5">
          <Progress value={progress} className="h-2" />
          <p className="text-xs text-muted-foreground text-center">
            Đang tải lên... {progress}%
          </p>
        </div>
      )}

      <Button
        type="submit"
        disabled={uploading}
        className="w-full min-h-[44px]"
      >
        {uploading ? 'Đang nộp...' : 'Nộp bài'}
      </Button>
    </form>
  );
}
